import { MenuItem } from 'primeng/api';
import { GlobalSearchGroup as RecordGlobalSearchGroup, GlobalSearchResult as RecordGlobalSearchResult } from 'src/app/services/global-search.service';
import { getChildItems, itemKey, menuIcon, normalizeRouterLink } from './app-menu.utils';
import { normalizeSearchText, scoreTextMatch } from './text-search.utils';

interface GlobalSearchPageResult {
    id: string;
    label: string;
    icon: string;
    route: string;
    parentLabel: string;
    searchText: string;
}

export interface GlobalSearchViewResult {
    id: string;
    type: string;
    label: string;
    icon: string;
    route: string;
    subtitle: string;
    source: 'page' | 'record';
}

export interface GlobalSearchViewGroup {
    key: string;
    label: string;
    results: GlobalSearchViewResult[];
}

export function buildPageGlobalSearchGroups(items: MenuItem[], query: string): GlobalSearchViewGroup[] {
    const normalizedTerm = normalizeSearchText(query);

    if (!normalizedTerm) {
        return [];
    }

    const results = flattenPageGlobalSearchResults(items)
        .filter(result => scorePageGlobalSearchResult(result, normalizedTerm) > 0)
        .sort((left, right) => comparePageGlobalSearchResults(left, right, normalizedTerm))
        .slice(0, 8)
        .map(result => ({
            id: result.id,
            type: 'PAGE',
            label: result.label,
            icon: result.icon,
            route: result.route,
            subtitle: result.parentLabel || 'Page',
            source: 'page'
        } satisfies GlobalSearchViewResult));

    return results.length ? [{
        key: 'pages',
        label: 'Pages',
        results
    }] : [];
}

export function mergeGlobalSearchGroups(
    pageGroups: GlobalSearchViewGroup[],
    recordGroups: RecordGlobalSearchGroup[]
): GlobalSearchViewGroup[] {
    const mappedRecordGroups = (recordGroups ?? [])
        .map(group => ({
            key: group.key,
            label: group.label,
            results: (group.results ?? []).map(toRecordSearchResult)
        }))
        .filter(group => group.results.length > 0);

    return [...pageGroups, ...mappedRecordGroups];
}

function toRecordSearchResult(result: RecordGlobalSearchResult): GlobalSearchViewResult {
    return {
        id: result.id,
        type: result.type,
        label: result.label,
        icon: result.icon || 'pi pi-search',
        route: result.route,
        subtitle: result.subtitle || 'Record',
        source: 'record'
    };
}

function flattenPageGlobalSearchResults(items: MenuItem[], ancestorLabels: string[] = [], keyParts: string[] = []): GlobalSearchPageResult[] {
    return items
        .filter(item => item.visible !== false)
        .flatMap((item, index) => {
            const itemLabel = item.label ?? 'Untitled';
            const itemKeyParts = [...keyParts, String(index)];
            const nextAncestors = [...ancestorLabels, itemLabel];
            const route = normalizeRouterLink(item.routerLink);
            const children = flattenPageGlobalSearchResults(getChildItems(item), nextAncestors, itemKeyParts);
            const result = route ? [{
                id: item.id ?? itemKey(item, itemKeyParts),
                label: itemLabel,
                icon: menuIcon(item),
                route,
                parentLabel: ancestorLabels.join(' / '),
                searchText: normalizeSearchText([itemLabel, ancestorLabels.join(' '), route].filter(Boolean).join(' '))
            } satisfies GlobalSearchPageResult] : [];

            return [...result, ...children];
        });
}

function comparePageGlobalSearchResults(left: GlobalSearchPageResult, right: GlobalSearchPageResult, normalizedTerm: string): number {
    return scorePageGlobalSearchResult(right, normalizedTerm) - scorePageGlobalSearchResult(left, normalizedTerm)
        || left.label.localeCompare(right.label)
        || left.route.localeCompare(right.route);
}

function scorePageGlobalSearchResult(result: GlobalSearchPageResult, normalizedTerm: string): number {
    const label = normalizeSearchText(result.label);
    const parentLabel = normalizeSearchText(result.parentLabel);
    const route = normalizeSearchText(result.route);

    const labelScore = scoreTextMatch(label, normalizedTerm, 1000);
    const routeScore = scoreTextMatch(route, normalizedTerm, 100);
    const parentScore = scoreTextMatch(parentLabel, normalizedTerm, 10);

    return labelScore + routeScore + parentScore;
}
